import React, { useEffect, useState } from "react";
import { api, money } from "../../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import DataTable from "../../components/admin/DataTable";

export default function AdminReports() {
  const [sales, setSales] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [salesLoading, setSalesLoading] = useState(true);
  const [tabsLoading, setTabsLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let alive = true;
    setSalesLoading(true);
    api.get(`/admin/reports/sales?days=${days}`)
      .then(({ data }) => { if (alive) setSales(data); })
      .catch(() => { if (alive) setSales({ orders: 0, gmv: 0, avg_order_value: 0, by_day: [] }); })
      .finally(() => { if (alive) setSalesLoading(false); });
    return () => { alive = false; };
  }, [days]);

  useEffect(() => {
    let alive = true;
    setTabsLoading(true);
    Promise.all([
      api.get("/admin/reports/sellers").catch(() => ({ data: { rows: [] } })),
      api.get("/admin/reports/products").catch(() => ({ data: { rows: [] } })),
      api.get("/admin/reports/customers").catch(() => ({ data: { rows: [] } })),
      api.get("/admin/reports/coupons").catch(() => ({ data: { rows: [] } })),
    ]).then(([s, p, c, co]) => {
      if (!alive) return;
      setSellers(s.data.rows || []);
      setProducts(p.data.rows || []);
      setCustomers(c.data.rows || []);
      setCoupons(co.data.rows || []);
    }).finally(() => { if (alive) setTabsLoading(false); });
    return () => { alive = false; };
  }, []);

  const sellerCols = [
    { key: "shop_name", label: "Shop" },
    { key: "status", label: "Status", render: (r) => <span className="uppercase text-xs">{r.status}</span> },
    { key: "orders", label: "Orders" },
    { key: "units_sold", label: "Units" },
    { key: "gross", label: "Gross", render: (r) => money(r.gross) },
    { key: "payout", label: "Payout", render: (r) => money(r.payout) },
  ];
  const productCols = [
    { key: "title", label: "Product" },
    { key: "qty_sold", label: "Qty Sold" },
    { key: "gross", label: "Gross", render: (r) => money(r.gross) },
  ];
  const customerCols = [
    { key: "name", label: "Name", render: (r) => r.name || "—" },
    { key: "phone", label: "Phone" },
    { key: "orders", label: "Orders" },
    { key: "spent", label: "Spent", render: (r) => money(r.spent) },
    { key: "last_order_at", label: "Last Order", render: (r) => (r.last_order_at || "").slice(0, 10) },
  ];
  const couponCols = [
    { key: "code", label: "Code", render: (r) => <span className="font-mono">{r.code}</span> },
    { key: "discount_type", label: "Type" },
    { key: "value", label: "Value", render: (r) => r.discount_type === "percent" ? `${r.value}%` : `₹${r.value}` },
    { key: "used_count", label: "Used" },
    { key: "is_active", label: "Active", render: (r) => r.is_active ? "Yes" : "No" },
  ];

  return (
    <div data-testid="admin-reports-page">
      <div className="overline mb-2">Insights</div>
      <h1 className="font-display text-4xl mb-8">Reports</h1>

      <Tabs defaultValue="sales">
        <TabsList className="rounded-none bg-transparent border-b border-[hsl(var(--kb-border))] p-0 h-auto w-full justify-start">
          <TabsTrigger value="sales" className="rounded-none px-4 py-2" data-testid="report-tab-sales">Sales</TabsTrigger>
          <TabsTrigger value="sellers" className="rounded-none px-4 py-2" data-testid="report-tab-sellers">Sellers</TabsTrigger>
          <TabsTrigger value="products" className="rounded-none px-4 py-2" data-testid="report-tab-products">Products</TabsTrigger>
          <TabsTrigger value="customers" className="rounded-none px-4 py-2" data-testid="report-tab-customers">Customers</TabsTrigger>
          <TabsTrigger value="coupons" className="rounded-none px-4 py-2" data-testid="report-tab-coupons">Coupons</TabsTrigger>
        </TabsList>

        <TabsContent value="sales" className="mt-6">
          <div className="flex gap-3 mb-4">
            {[7, 30, 90].map((d) => (
              <button key={d} onClick={() => setDays(d)} className={`chip ${days === d ? "bg-[hsl(var(--kb-primary))] text-white border-[hsl(var(--kb-primary))]" : ""}`} data-testid={`sales-days-${d}`}>
                Last {d} days
              </button>
            ))}
          </div>
          {salesLoading && <div className="text-center p-6 text-[hsl(var(--kb-muted))]" data-testid="sales-loading">Loading sales data…</div>}
          {sales && !salesLoading && (
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white border border-[hsl(var(--kb-border))] p-6"><div className="overline mb-2">GMV</div><div className="font-display text-3xl">{money(sales.gmv)}</div></div>
              <div className="bg-white border border-[hsl(var(--kb-border))] p-6"><div className="overline mb-2">Orders</div><div className="font-display text-3xl">{sales.orders}</div></div>
              <div className="bg-white border border-[hsl(var(--kb-border))] p-6"><div className="overline mb-2">AOV</div><div className="font-display text-3xl">{money(sales.avg_order_value)}</div></div>
            </div>
          )}
          {sales && !salesLoading && (
            <div className="bg-white border border-[hsl(var(--kb-border))] p-6 overflow-x-auto">
              <div className="overline mb-4">By day</div>
              <div className="flex items-end gap-1 h-40 min-w-[600px]">
                {sales.by_day.map((d) => {
                  const max = Math.max(...sales.by_day.map((x) => x.total), 1);
                  return (
                    <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full bg-[hsl(var(--kb-primary))]/80" style={{ height: `${(d.total / max) * 100}%` }} title={`${d.date}: ${money(d.total)}`} />
                      <div className="text-[9px] text-[hsl(var(--kb-muted))]">{d.date.slice(5)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="sellers" className="mt-6">
          <DataTable columns={sellerCols} rows={sellers} loading={tabsLoading} emptyLabel="No sellers" testid="sellers-report-table" />
        </TabsContent>
        <TabsContent value="products" className="mt-6">
          <DataTable columns={productCols} rows={products} loading={tabsLoading} emptyLabel="No product sales yet" testid="products-report-table" />
        </TabsContent>
        <TabsContent value="customers" className="mt-6">
          <DataTable columns={customerCols} rows={customers} loading={tabsLoading} emptyLabel="No customers yet" testid="customers-report-table" />
        </TabsContent>
        <TabsContent value="coupons" className="mt-6">
          <DataTable columns={couponCols} rows={coupons} loading={tabsLoading} emptyLabel="No coupons" testid="coupons-report-table" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
